import { APP_ROUTES } from "../constants";
import { LoadingPage } from "../pages/LoadingPage.tsx";
import { WelcomePage } from "../pages/WelcomePage.tsx";
import { LandingPage } from "../pages/LandingPage.tsx";
import { HomePage } from "../pages/HomePage.tsx";
import { TransferPage } from "../pages/TransferPage.tsx";
import { AddCardPage } from "../pages/AddCardPage.tsx";
import { BillingAddressPage } from "../pages/BillingAddressPage.tsx";
import { AddCardProcessingPage } from "../pages/AddCardProcessingPage.tsx";
import { createElement } from "react";
import { Navigate } from "react-router-dom";
import { CipPage } from "../pages/CipPage.tsx";
import { PhoneNumberPage } from "../pages/PhoneNumberPage.tsx";
import { SendingMoneyPage } from "../pages/SendingMoneyPage.tsx";
import { HowToDepositCashPage } from "../pages/HowToDepositCashPage.tsx";
import { StoresMapPage } from "../pages/StoresMapPage.tsx";
import { ReceiptSummaryPage } from "../pages/ReceiptSummaryPage.tsx";
import { LegalPage } from "../pages/LegalPage.tsx";
import { TermDetailPage } from "../pages/TermDetailPage.tsx";
import { SmsTermsPage } from "../pages/SmsTermsPage.tsx";
import { ErrorPage } from "../shared/error/pages/ErrorPage";
import { SessionExpiredPage } from "../shared/error/pages/SessionExpiredPage";
import { CancellationInProgressPage } from "../shared/error/pages/CancellationInProgressPage";
import { CancellationFailedPage } from "../shared/error/pages/CancellationFailedPage";
import { ServiceUnavailablePage } from "../shared/error/pages/ServiceUnavailablePage";
import { MaintenancePage } from "../shared/error/pages/MaintenancePage";
import { SupportPage } from "../pages/SupportPage.tsx";

export const formatMoney = (value: string) => {
  if (!value || value === ".00") return "";
  value = value.replace(/[^\d.]/g, "");
  const [intPart, decPart = ""] = value.split(".");
  const decimals = (decPart + "00").slice(0, 2);
  return `${intPart || "0"}.${decimals}`;
};

export const getCurrentDateFormatted = (): string => {
  const date = new Date();

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);

  return `${day}/${month}/${year}`;
};

export type amountFormatterProps = (
  amount: number,
  currency?: string,
  numberFormatOptions?: {
    country?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
) => string;

export const amountFormatter: amountFormatterProps = (
  amount,
  currency = "USD",
  {
    country = "en-US",
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = {}
) =>
  new Intl.NumberFormat(country, {
    style: "decimal",
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount);

/** Decimal amount + ISO code; USD also includes `$`. */
export function formatLabeledAmount(
  amount: number,
  currency = "USD",
  numberFormatOptions?: {
    country?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string {
  const formatted = amountFormatter(amount, currency, numberFormatOptions);
  const code = currency.toUpperCase();
  return code === "USD" ? `$${formatted} ${code}` : `${formatted} ${code}`;
}

/** Hero typography: en-US `25.00` split; `$` + ISO for USD, ISO suffix otherwise. */
export function formatHeroAmount(amount: number, currency: string) {
  const formatted = amountFormatter(amount, currency, {
    country: "en-US",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const [intPart, decPart = "00"] = formatted.split(".");
  const code = currency.toUpperCase();
  return {
    prefix: code === "USD" ? "$" : "",
    suffix: ` ${code}`,
    intPart,
    decPart,
  };
}

const CashDepositRedirect = () =>
  createElement(Navigate, { to: APP_ROUTES.HOME_PAGE, replace: true });

export interface IAppRoutes {
  path: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: any;
}

export const routesList: IAppRoutes[] = [
  // Magic-link entry: the flow is encoded in the linkId itself
  { path: APP_ROUTES.LOADING_PAGE, Component: LoadingPage },
  { path: "/:linkId", Component: LoadingPage },
  { path: APP_ROUTES.WELCOME_PAGE, Component: WelcomePage },
  { path: APP_ROUTES.LANDING_PAGE, Component: LandingPage },
  { path: APP_ROUTES.HOME_PAGE, Component: HomePage },
  { path: APP_ROUTES.RECEIPT_SUMMARY_PAGE, Component: ReceiptSummaryPage },
  { path: APP_ROUTES.TRANSFER_PAGE, Component: TransferPage },
  { path: APP_ROUTES.ADD_CARD_PAGE, Component: AddCardPage },
  { path: APP_ROUTES.ADD_CARD_ADDRESS_PAGE, Component: BillingAddressPage },
  { path: APP_ROUTES.ADD_CARD_PROCESSING_PAGE, Component: AddCardProcessingPage },
  { path: APP_ROUTES.CIP_PAGE, Component: CipPage },
  { path: APP_ROUTES.PHONE_PAGE, Component: PhoneNumberPage },
  { path: APP_ROUTES.CASH_DEPOSIT_PAGE, Component: CashDepositRedirect },
  { path: APP_ROUTES.SENDING_MONEY_PAGE, Component: SendingMoneyPage },
  { path: APP_ROUTES.HOW_TO_DEPOSIT_CASH_PAGE, Component: HowToDepositCashPage },
  { path: APP_ROUTES.STORES_MAP_PAGE, Component: StoresMapPage },
  { path: APP_ROUTES.SUPPORT_PAGE, Component: SupportPage },
  { path: APP_ROUTES.LEGAL_PAGE, Component: LegalPage },
  { path: APP_ROUTES.TERM_DETAIL_PAGE, Component: TermDetailPage },
  { path: APP_ROUTES.SMS_TERMS_PAGE, Component: SmsTermsPage },
  { path: APP_ROUTES.ERROR_PAGE, Component: ErrorPage },
  { path: APP_ROUTES.SESSION_EXPIRED_PAGE, Component: SessionExpiredPage },
  {
    path: APP_ROUTES.CANCELLATION_IN_PROGRESS_PAGE,
    Component: CancellationInProgressPage,
  },
  {
    path: `${APP_ROUTES.CANCELLATION_FAILED_PAGE}/:operationId`,
    Component: CancellationFailedPage,
  },
  {
    path: APP_ROUTES.SERVICE_UNAVAILABLE_PAGE,
    Component: ServiceUnavailablePage,
  },
  {
    path: APP_ROUTES.MAINTENANCE_PAGE,
    Component: MaintenancePage,
  },
];
